import React from 'react';
import { SliderField, WEBSHOP_MAX_WIDTH_PX } from '../../_webshopUi';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';

/**
 * "Footer width" card — controls the footer's container size and background
 * stretching.
 *
 * The value is **shared** with GlobalWidthSettingsModal — both modals write
 * the same underlying settings (SHOP_MAX_WIDTH_FOOTER and
 * SHOP_MAX_WIDTH_FOOTER_FW_BG). Editing here flows back through
 * `onChangeGlobalFooterWidth` to the parent's globalWidths state so the
 * other modal stays in sync.
 *
 * Props:
 *   globalFooterWidth:        { width: number, bgFullWidth: bool } — current value
 *   onChangeGlobalFooterWidth(patch) — partial update, e.g. { width: 1500 }
 */
const FooterWidthCard = ({ globalFooterWidth, onChangeGlobalFooterWidth, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const width       = Number(globalFooterWidth?.width) || 1500;
    const bgFullWidth = globalFooterWidth?.bgFullWidth !== false;
    const emit = (patch) => onChangeGlobalFooterWidth && onChangeGlobalFooterWidth(patch);

    return (
        <ConfigCard
            anchor="cfg-card-footer-width"
            title={tr('cfg_footer_width_title', 'Footer width')}
            description={tr('cfg_footer_width_desc', 'Customise the footer. The bg can stretch to edge while the content stays centered.')}
            theme={theme}
        >
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {tr('cfg_footer_width_max', 'Maximum content width')}
                </Typography>
                <SliderField
                    value={width}
                    min={400}
                    max={WEBSHOP_MAX_WIDTH_PX}
                    step={20}
                    unit="px"
                    label={tr('cfg_footer_width_max', 'Maximum content width')}
                    onChange={(v) => emit({ width: v })}
                />
            </Box>

            <FormControlLabel
                control={
                    <Switch color="success"
                        size="small"
                        checked={bgFullWidth}
                        onChange={(e) => emit({ bgFullWidth: e.target.checked })}
                    />
                }
                label={<Typography variant="body2">{tr('cfg_footer_width_bg_full', 'Background stretches to viewport edges')}</Typography>}
            />
        </ConfigCard>
    );
};

FooterWidthCard.propTypes = {
    globalFooterWidth: PropTypes.shape({
        width:       PropTypes.number,
        bgFullWidth: PropTypes.bool,
    }),
    onChangeGlobalFooterWidth: PropTypes.func,
    theme: PropTypes.string,
    t:     PropTypes.func,
};

export default FooterWidthCard;
